interface EnvVar {
  name: string
  description: string
  required: boolean
  defaultValue?: string
}

interface ValidationResult {
  isValid: boolean
  missingVars: string[]
  errors: string[]
}

const REQUIRED_ENV_VARS: EnvVar[] = [
  {
    name: 'VITE_CLERK_PUBLISHABLE_KEY',
    description: 'Clerk authentication publishable key',
    required: true,
  },
  {
    name: 'VITE_ENVIRONMENT',
    description: 'Environment (DEVELOPMENT, STAGING, PRODUCTION)',
    required: true,
  },
  {
    name: 'VITE_API_URL',
    description: 'Backend API URL',
    required: true,
  },
  {
    name: 'VITE_SOCKET_URL',
    description: 'Backend Socket URL',
    required: false,
  },
  {
    name: 'VITE_APP_URL',
    description: 'Frontend App URL',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_API_KEY',
    description: 'Firebase API Key',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_AUTH_DOMAIN',
    description: 'Firebase Auth Domain',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_PROJECT_ID',
    description: 'Firebase Project ID',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_STORAGE_BUCKET',
    description: 'Firebase Storage Bucket',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_MESSAGING_SENDER_ID',
    description: 'Firebase Messaging Sender ID',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_APP_ID',
    description: 'Firebase App ID',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_MEASUREMENT_ID',
    description: 'Firebase Measurement ID',
    required: false,
  },
  {
    name: 'VITE_FIREBASE_VAPID_KEY',
    description: 'Firebase VAPID Key for push notifications',
    required: false,
  },
  {
    name: 'VITE_ELEVENLABS_AGENT_ID',
    description: 'ElevenLabs Agent ID',
    required: false,
  },
  {
    name: 'VITE_SENTRY_ENABLED',
    description: 'Sentry enabled (true/false)',
    required: false,
  },
  {
    name: 'VITE_SENTRY_ORG',
    description: 'Sentry organization slug (for uploading source maps)',
    required: false,
  },
  {
    name: 'VITE_SENTRY_AUTH_TOKEN',
    description: 'Sentry authentication token (for source map uploads)',
    required: false,
  },
  {
    name: 'VITE_SENTRY_DSN',
    description: 'Sentry Data Source Name (DSN, used by the app to report errors)',
    required: false,
  },
  {
    name: 'VITE_SENTRY_TRACES_SAMPLE_RATE',
    description: 'Sentry tracing sample rate (0.0-1.0, controls # of performance traces sent)',
    required: false,
  },
  {
    name: 'VITE_SENTRY_REPLAY_SAMPLE_RATE',
    description: 'Sentry Session Replay sample rate for normal sessions (0.0-1.0)',
    required: false,
  },
  {
    name: 'VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE',
    description: 'Sentry Session Replay sample rate for error sessions (0.0-1.0)',
    required: false,
  },
  {
    name: 'VITE_CLARITY_ENABLED',
    description: 'Clarity enabled (true/false)',
    required: false,
  },
  {
    name: 'VITE_CLARITY_PROJECT_ID',
    description: 'Clarity project ID',
    required: false,
  },
]

/**
 * Validates all required environment variables
 * @returns ValidationResult with validation status and error details
 */
export function validateEnvironment(): ValidationResult {
  const missingVars: string[] = []
  const errors: string[] = []

  for (const envVar of REQUIRED_ENV_VARS) {
    const value = import.meta.env[envVar.name]

    if (envVar.required && (!value || value.trim() === '')) {
      missingVars.push(envVar.name)
      errors.push(`❌ ${envVar.name}: ${envVar.description} is required but not set`)
    }
  }

  const isValid = missingVars.length === 0

  if (!isValid) {
    errors.unshift('🚨 Environment validation failed!')
    errors.push('')
    errors.push('Please add the missing environment variables to your .env file:')
    errors.push('')

    for (const missingVar of missingVars) {
      const envVar = REQUIRED_ENV_VARS.find((v) => v.name === missingVar)
      errors.push(`${missingVar}=your_value_here  # ${envVar?.description}`)
    }

    errors.push('')
    errors.push('See .env.example for a template of all required variables.')
  }

  return {
    isValid,
    missingVars,
    errors,
  }
}

/**
 * Gets all environment variables with their current values (for debugging)
 */
export function getEnvironmentInfo(): Record<string, string> {
  const envInfo: Record<string, string> = {}

  for (const envVar of REQUIRED_ENV_VARS) {
    const value = import.meta.env[envVar.name]
    envInfo[envVar.name] = value || 'NOT_SET'
  }

  return envInfo
}

/**
 * Throws an error with formatted environment validation errors
 */
export function validateEnvironmentOrThrow(): void {
  const result = validateEnvironment()

  if (!result.isValid) {
    const errorMessage = result.errors.join('\n')
    throw new Error(errorMessage)
  }
}
